import React from 'react'
import DashboardSectionCard from '@components/pages/Dashboard/DashboardSectionCard.component'
import DashboardStackedBarChart from '@components/pages/Dashboard/MetricCharts/DashboardStackedBarChart.component'
import DashboardSectionEmptyState from '@components/pages/Dashboard/DashboardSectionEmptyState.component'
import {
  FinanceGrossNetBarLegend,
  FinanceProductionSectionHeaderRight,
  FinanceProductionMetricsTiles,
  FINANCE_DUAL_BAR_CHART_PROPS,
} from '../../FinanceMetricTiles.component'
import type { RangeKey } from '@dataTypes/dashboard.types'
import { PRODUCTION_COLLECTION_SERIES } from '@constants/Dashboard/financeDashboard.constants'
import type { FinanceProductionPanel } from '../../../../../../types/financeDashboard.types'
import {
  financeTrendLine,
  formatChartCurrencyKUnits,
} from '@utils/dashboardFinance.util'
import {
  isDashboardSectionPending,
  shouldShowDashboardSectionEmpty,
} from '@utils/dashboardSectionEmpty.util'

type ProductionCardProps = {
  titleHref: string
  range: RangeKey
  data: FinanceProductionPanel
  loading?: boolean
  /** Production response has arrived (`data != null`). */
  hasPayload: boolean
  /** Summary KPIs or period chart buckets contain a nonzero value. */
  hasData: boolean
  /** Period chart buckets contain a nonzero value. */
  hasChartData: boolean
}

export function ProductionCard({
  titleHref,
  range,
  data: prodColl,
  loading = false,
  hasPayload,
  hasData,
  hasChartData,
}: ProductionCardProps): React.ReactElement {
  const isEmpty = shouldShowDashboardSectionEmpty(loading, hasData)
  const isChartEmpty = shouldShowDashboardSectionEmpty(loading, hasChartData)
  const pending = isDashboardSectionPending(loading, hasPayload)

  return (
    <DashboardSectionCard
      title="Production"
      titleHref={titleHref}
      className="dashboard-finance-production-card"
      loading={pending}
      skeleton={{ metrics: 4, block: 'chart' }}
      isEmpty={isEmpty}
      disableTitleLink={isEmpty}
      headerRight={
        <FinanceProductionSectionHeaderRight
          trendLine={financeTrendLine(
            range,
            prodColl.changeSignedPct,
            prodColl.changeAmt,
          )}
          goalLabel={prodColl.prodGoalLabel}
        />
      }
    >
      <div className="dashboard-finance-production">
        <FinanceProductionMetricsTiles prodColl={prodColl} />
        {isChartEmpty ? (
          <DashboardSectionEmptyState className="dashboard-section-empty--embed" />
        ) : (
          <div className="dashboard-finance-production-chart-wrap dashboard-finance-bar-chart-legend-spaced">
            <DashboardStackedBarChart
              data={prodColl.prodChart}
              series={[...PRODUCTION_COLLECTION_SERIES]}
              horizontal={false}
              stacked={false}
              valueFormatter={formatChartCurrencyKUnits}
              height={220}
              yAxisDomain={prodColl.prodYDomain}
              yAxisTicks={prodColl.prodYTicks}
              goalLineValue={prodColl.prodGoalLine}
              showLegend={false}
              className="dashboard-finance-production-bar-chart"
              {...FINANCE_DUAL_BAR_CHART_PROPS}
            />
            <FinanceGrossNetBarLegend />
          </div>
        )}
      </div>
    </DashboardSectionCard>
  )
}
